// components/Footer.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const Footer = () => {
  // State for storing dynamic content
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default links and data (fallback if API fails)
  const defaultLinks = {
    company: [
      { name: "About Us", href: "/about" },
      { name: "Contact Us", href: "/contact" },
    ],
    programs: [
      { name: "Classroom Program", href: "/classroom" },
      { name: "Online Foundation", href: "/online-foundation" },
    ],
    resources: [
      { name: "Blog", href: "/blog" },
    ],
    support: [
      { name: "FAQs", href: "/faqs" },
      { name: "Privacy Policy", href: "/privacy-policy" },
    ],
  };

  // Fetch footer content from API
  useEffect(() => {
    const fetchFooterContent = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://server.skysep.in/api/footer');
        setContent(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching footer content:', err);
        setError('Failed to load footer content');
      } finally {
        setLoading(false);
      }
    };

    fetchFooterContent();
  }, []);

  // If still loading or error occurred, use fallback content
  const links = content?.links || defaultLinks;
  const contactInfo = content?.contactInfo || [];
  const socialMedia = content?.socialMedia || [];
  const companyInfo = content?.companyInfo || {
    description: "Providing holistic solutions for personal growth and professional development through innovative learning programs."
  };
  const copyright = content?.copyright?.text || `© ${new Date().getFullYear()} Skyseps Holistics Pvt Ltd. All rights reserved.`;

  return (
    <footer className="bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Logo and Contact */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center mb-8">
              {companyInfo?.logo ? (
                <img
                  className="h-20 w-auto"
                  src={companyInfo.logo}
                  alt="SKYSEP"
                />
              ) : (
                <img
                  className="h-20 w-auto brightness-0 invert"
                  src={require('../assets/logo.png')}
                  alt="SKYSEP"
                />
              )}
            </Link>
            <div className="mb-4">
              <span className="text-2xl font-bold">SKYSEP</span>
              <div className="text-sm text-blue-200">Holistics Pvt Ltd</div>
            </div>
            <p className="text-blue-100 mb-8 leading-relaxed">
              {companyInfo.description}
            </p>
            <ul className="space-y-4">
              {contactInfo.map((item, index) => (
                <motion.li
                  key={index}
                  className="flex items-center"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <span className="flex-shrink-0 text-blue-200"
                        dangerouslySetInnerHTML={{ __html: item.iconSvg }}>
                  </span>
                  <span className="ml-3 text-blue-100">{item.text}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-200 mb-6">
              Company
            </h3>
            <ul className="space-y-3">
              {links.company && links.company.map((link, index) => (
                <motion.li
                  key={`company-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={link.href}
                    className="text-blue-100 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-200 mb-6">
              Programs
            </h3>
            <ul className="space-y-3">
              {links.programs && links.programs.map((link, index) => (
                <motion.li
                  key={`programs-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={link.href}
                    className="text-blue-100 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-200 mb-6">
              Resources
            </h3>
            <ul className="space-y-3">
              {links.resources && links.resources.map((link, index) => (
                <motion.li
                  key={`resources-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={link.href}
                    className="text-blue-100 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-200 mb-6">
              Support
            </h3>
            <ul className="space-y-3">
              {links.support && links.support.map((link, index) => (
                <motion.li
                  key={`support-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={link.href}
                    className="text-blue-100 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-blue-400/30 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex space-x-6 mb-6 md:mb-0">
              {socialMedia.map((item, index) => (
                <a
                  key={`social-${index}`}
                  href={item.href}
                  className="text-blue-200 hover:text-white transition-colors duration-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">{item.name}</span>
                  <span dangerouslySetInnerHTML={{ __html: item.iconSvg }}></span>
                </a>
              ))}
              {/* Add default social icons if none provided */}
              {socialMedia.length === 0 && (
                <>
                  <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                    </svg>
                  </a>
                  <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
                    </svg>
                  </a>
                  <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a href="#" className="text-blue-200 hover:text-white transition-colors duration-200">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16.98 0a6.9 6.9 0 0 1 5.08 1.98A6.94 6.94 0 0 1 24 7.02v9.96c0 2.08-.68 3.87-1.98 5.13A7.14 7.14 0 0 1 16.94 24H7.06a7.06 7.06 0 0 1-5.03-1.89A6.96 6.96 0 0 1 0 16.94V7.02C0 2.8 2.8 0 7.02 0h9.96zm.05 2.23H7.06c-1.45 0-2.7.43-3.53 1.25a4.82 4.82 0 0 0-1.3 3.54v9.92c0 1.5.43 2.7 1.3 3.58a5 5 0 0 0 3.53 1.25h9.88a5 5 0 0 0 3.53-1.25 4.73 4.73 0 0 0 1.4-3.54V7.02a5 5 0 0 0-1.3-3.49 4.82 4.82 0 0 0-3.54-1.3zM12 5.76c3.39 0 6.2 2.8 6.2 6.2a6.2 6.2 0 0 1-12.4 0 6.2 6.2 0 0 1 6.2-6.2zm0 2.22a3.99 3.99 0 0 0-3.97 3.97A3.99 3.99 0 0 0 12 15.92a3.99 3.99 0 0 0 3.97-3.97A3.99 3.99 0 0 0 12 7.98zm6.44-3.77a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z" />
                    </svg>
                  </a>
                </>
              )}
            </div>
            <div className="flex flex-col md:flex-row md:items-center">
              <p className="text-sm text-blue-100">
                {copyright}
              </p>
              <div className="mt-3 md:mt-0 md:ml-6 flex space-x-6">
                <Link
                  to="/privacy-policy"
                  className="text-sm text-blue-100 hover:text-white transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-sm text-blue-100 hover:text-white transition-colors duration-200">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;